import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { pick } from "../i18n/strings";

function format(num) {
  return num.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
}
function formatExp(v) {
  const d = v.replace(/\D/g, "").slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

export default function Payment() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();

  const [booking, setBooking] = useState(null);
  const [name, setName] = useState("");
  const [number, setNumber] = useState("4242 4242 4242 4242");
  const [expiry, setExpiry] = useState("12/27");
  const [cvc, setCvc] = useState("123");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((res) => setBooking(res.data));
  }, [bookingId]);

  if (!booking) return null;

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      await new Promise((r) => setTimeout(r, 900)); // simulate latency
      await api.post(`/bookings/${bookingId}/pay`, {
        booking_id: bookingId,
        card_name: name || "Test Holder",
        card_number: number,
        card_expiry: expiry,
        card_cvc: cvc,
      });
      navigate(`/confirmation/${bookingId}`, { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-6 pt-6 pb-10" data-testid="payment-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="payment-back"
        className="inline-flex items-center gap-2 text-brand-brown/70 text-xs tracking-[0.25em] uppercase font-bold"
      >
        <ArrowLeft size={14} /> {t("summary")}
      </button>

      <header className="mt-8">
        <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-tan">
          {t("proceed_to_payment")}
        </p>
        <h1 className="font-serif text-4xl text-brand-brown mt-2 leading-[1.05]">
          €{booking.total_eur}
        </h1>
        <p className="text-sm text-brand-brown/70 mt-2">
          {pick(booking.experience_snapshot.title, lang)} · {booking.guests}{" "}
          {t("guests")}
        </p>
      </header>

      <div
        className="mt-6 rounded-xl bg-brand-green/10 border border-brand-green/30 text-brand-green text-xs px-4 py-3 tracking-wider"
        data-testid="mock-notice"
      >
        {t("mock_notice")}
      </div>

      <form
        className="mt-8 space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          pay();
        }}
      >
        <Field
          label={t("card_name")}
          value={name}
          onChange={setName}
          testid="card-name"
          placeholder="Jordi Català"
        />
        <Field
          label={t("card_number")}
          value={number}
          onChange={(v) => setNumber(format(v))}
          testid="card-number"
          inputMode="numeric"
        />
        <div className="grid grid-cols-2 gap-4">
          <Field
            label={t("card_expiry")}
            value={expiry}
            onChange={(v) => setExpiry(formatExp(v))}
            testid="card-expiry"
            inputMode="numeric"
          />
          <Field
            label={t("card_cvc")}
            value={cvc}
            onChange={(v) => setCvc(v.replace(/\D/g, "").slice(0, 4))}
            testid="card-cvc"
            inputMode="numeric"
          />
        </div>

        {error && (
          <p className="text-sm text-destructive" data-testid="payment-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="payment-submit"
          className="w-full py-4 rounded-2xl bg-brand-brown text-brand-cream font-bold tracking-[0.18em] uppercase text-sm transition-transform active:scale-[0.98] disabled:opacity-60"
        >
          {busy ? "…" : `${t("pay_now")} · €${booking.total_eur}`}
        </button>

        <div className="flex items-center justify-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-brand-tan">
          <ShieldCheck size={14} strokeWidth={1.5} />
          <span>{t("payment_secure")}</span>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, inputMode, placeholder }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.3em] uppercase font-bold text-brand-tan">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode={inputMode}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-brand-tan/40 focus:border-brand-brown py-2.5 outline-none text-brand-brown font-medium text-lg transition-colors"
      />
    </label>
  );
}
